import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { ReactComponent as Delete } from "../../assets/images/svg/Delete.svg";
import Swal from "sweetalert2";
import useAuth from "../../hooks/useAuth";

import { ReactComponent as Bullet } from "../../assets/images/svg/bullets.svg";


const SelectDID = ({
    dids = [],
    did = [],
    setData,
    setActive,
    caller_id_mode,
    next,
    back,
    predictiveDialer=Boolean
}) => {
    const [lockedChannelId, setLockedChannelId] = useState(null);

    console.log(`dids (${typeof dids}): ${JSON.stringify(dids)}\n`)
    console.log(`did (${typeof did}): ${JSON.stringify(did)}\n`)

    // Get user authentication data
    const { auth } = useAuth();
    const userId = auth?.user_details?.id;

    // State management
    const [selectedDids, setSelectedDids] = useState(did);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const dropdownRef = useRef(null);
    const [filtered_Dids, setFiltered_Dids] = useState([]);


    useEffect(() => {
        if (!predictiveDialer) return;
        if (!did || did.length === 0) return;

        const channelId = did[0].channelId;

        setLockedChannelId(channelId);
        setSelectedDids(did);

        setFiltered_Dids(
            dids.filter(d => d.channelId === channelId)
        );

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);


    useEffect(() => {
        if (!predictiveDialer) {
            setFiltered_Dids(dids);
            return;
        }

        if (selectedDids.length === 0) {
            setFiltered_Dids(dids);
            return;
        }

        const channelId = selectedDids[0].channelId;
        setFiltered_Dids(dids.filter(d => d.channelId === channelId));
    }, [selectedDids, dids, predictiveDialer]);

    // Memoized available DIDs (exclude already selected ones)
    const availableDids = useMemo(() => {
        return dids.filter(
            didItem => !selectedDids.some(sd=> (sd?.did ?? sd) === didItem.did

            )).filter(
                d=>{
                    if(!predictiveDialer)return true;
                    if(!lockedChannelId) return true;
                    return d.channelId ===lockedChannelId;
                }
            );
    }, [dids, selectedDids]);

    // Filtered DIDs based on search term
    const filteredDids = useMemo(() => {
        if (!searchTerm) return availableDids;
        return availableDids.filter(didItem =>
            didItem.did.toLowerCase().includes(searchTerm.toLowerCase())
        );
    }, [availableDids, searchTerm]);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsDropdownOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    // Handle individual DID selection
    const handleDidSelect = useCallback((didValue,channelId) => {
        if (selectedDids.some(d=>d.did === didValue)) return;

        console.log(`selectedDids (${typeof selectedDids}): ${selectedDids.length}\n`)
        console.log(`selectedDids (${typeof selectedDids}): ${availableDids.length}\n`)

        if (predictiveDialer && selectedDids.length === 0) {
            setLockedChannelId(channelId);
        }

        const newSelectedDids = [...selectedDids, {did:didValue,channelId}];
        console.log(`newSelectedDids (${typeof newSelectedDids}) : ${newSelectedDids}\n`)
        setSelectedDids(newSelectedDids);

        // Update parent component data
        setData(prevState => ({
            ...prevState,
            dids: newSelectedDids
        }));

        // Clear search term
        setSearchTerm("");
    }, [selectedDids, setData]);

    // Handle select all filtered DIDs
    const handleSelectAll = useCallback(() => {
        const didsToAdd = filteredDids.filter(did => !selectedDids.includes(did));
        if (didsToAdd.length === 0) return;

        if (predictiveDialer && selectedDids.length === 0) {
            Swal.fire({
                title: "Select a DID First",
                text: "Please select one DID first. After that, you can use Select All to add the rest.",
                icon: "info",
                confirmButtonText: "Got it"
            });
            return;
        }

        const newSelectedDids = [...selectedDids, ...didsToAdd];
        setSelectedDids(newSelectedDids);

        // Update parent component data
        setData(prevState => ({
            ...prevState,
            dids: newSelectedDids
        }));

        // Clear search and close dropdown
        setSearchTerm("");
        setIsDropdownOpen(false);
    }, [selectedDids, filteredDids, setData]);

    // Handle clear all selected DIDs
    const handleClearAll = useCallback(() => {
        setSelectedDids([]);

        if (predictiveDialer) {
            setLockedChannelId(null);
        }

        setData(prevState => ({
            ...prevState,
            dids: []
        }));
    }, [setData]);

    // Handle DID deletion with confirmation
    const handleDelete = useCallback(async (didToDelete, index) => {
            try {
                const result = await Swal.fire({
                    title: "Are you sure you want to delete this DID?",
                    text: "This action cannot be undone.",
                    icon: "warning",
                    showCancelButton: true,
                    confirmButtonColor: "#3085d6",
                    cancelButtonColor: "#d33",
                    confirmButtonText: "Delete",
                    cancelButtonText: "Cancel"
                });
    
                if (result.isConfirmed) {
                    const updatedSelectedDids = selectedDids.filter(d => ( d.did ?? d ) !== didToDelete);
                    setSelectedDids(updatedSelectedDids);
    
                    if (predictiveDialer && updatedSelectedDids.length === 0) {
                        setLockedChannelId(null);
                    }
    
                    // Update parent component data
                    setData(prevState => ({
                        ...prevState,
                        dids: updatedSelectedDids
                    }));
    
                    // Show success message
                    Swal.fire({
                        title: "Deleted!",
                        text: "DID has been removed.",
                        icon: "success",
                        timer: 2000,
                        showConfirmButton: false
                    });
                }
            } catch (error) {
                console.error("Error deleting DID:", error);
                Swal.fire({
                    title: "Error",
                    text: "Failed to delete DID. Please try again.",
                    icon: "error"
                });
            }
        }, [selectedDids, setData]);

    // Handle next step navigation
    const handleNextStep = useCallback(() => {
        if (selectedDids.length === 0) {
            Swal.fire({
                title: "No DIDs Selected",
                text: "Please select at least one DID to continue.",
                icon: "warning"
            });
            return;
        }
        setActive(next);
    }, [selectedDids.length, setActive, next]);

    // Handle back navigation
    const handleBack = useCallback(() => {
        setActive(back);
    }, [setActive, back]);

    const hasMultipleChannels = (dids) => {
        const channels = new Set(dids.map(d => d.channelId).filter(Boolean));
        return channels.size > 1;
    };

    useEffect(() => {
        if (!predictiveDialer) return;
        if (!selectedDids || selectedDids.length === 0) return;

        if (hasMultipleChannels(selectedDids)) {
            Swal.fire({
                title: "Invalid DID Selection",
                text: "Predictive dialer allows DIDs from only one channel. Please select again.",
                icon: "warning",
                confirmButtonText: "Clear & Re-select",
                allowOutsideClick: false,
                allowEscapeKey: false
            }).then(() => {
                // 🔥 Reset everything
                setSelectedDids([]);
                setLockedChannelId(null);

                setData(prev => ({
                    ...prev,
                    dids: []
                }));
            });
        }
        // run ONLY on mount
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);



    return (
        <div className="max-w-5xl p-6 ml-5">
            {/* Header Section */}
            <div className="mb-8">
                <p className="text-sm text-slate-400 mb-2">Step 5/7</p>
                <h1 className="text-2xl font-bold text-gray-800 mb-2">Select DIDs</h1>
                <p className="text-sm text-slate-400 pb-6 border-b-2 border-gray-200">
                    Please select multiple DIDs for your campaign
                </p>
            </div>
            <div className="flex items-center space-x-4">
                {/* Multi-Select DID Selection */}
                <div className="mb-8 w-full">
                    <div className="flex items-center justify-between mb-3">
                        <label className="block text-md text-slate-600 font-medium mb-3 flex items-center gap-2">
                            <Bullet />
                            Available DIDs ({availableDids.length} remaining)
                        </label>
                        {selectedDids.length > 0 && (
                            <button
                                onClick={handleClearAll}
                                className="text-sm text-red-500 hover:text-red-700 font-medium"
                            >
                                Clear All ({selectedDids.length})
                            </button>
                        )}
                    </div>

                    {/* Multi-Select Dropdown */}
                    <div className="relative" ref={dropdownRef}>
                        <button
                            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                            disabled={availableDids.length === 0}
                            className="w-full h-12 px-4 py-2.5 text-left text-gray-700 bg-white border border-gray-300 rounded-lg shadow-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-200 flex items-center justify-between disabled:bg-gray-100 disabled:cursor-not-allowed"
                        >
                            <span>
                                {availableDids.length === 0
                                    ? "No DIDs available"
                                    : `Select DIDs (${selectedDids.length} selected)`
                                }
                            </span>
                            <svg
                                className={`w-5 h-5 transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`}
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                        </button>

                        {/* Dropdown Content */}
                        {isDropdownOpen && availableDids.length > 0 && (
                            <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-[20rem] overflow-hidden">
                                {/* Search Input */}
                                <div className="p-3 border-b border-gray-200">
                                    <input
                                        type="text"
                                        placeholder="Search DIDs..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-200"
                                    />
                                </div>

                                {/* Select All Option */}
                                {filteredDids.length > 1 && (
                                    <div className="px-3 py-2 border-b border-gray-100">
                                        <button
                                            onClick={handleSelectAll}
                                            className="w-full text-left text-sm text-blue-600 hover:text-blue-800 font-medium"
                                        >
                                            Select All Filtered ({filteredDids.length})
                                        </button>
                                    </div>
                                )}

                                {/* DID Options */}
                                <div className="max-h-48 overflow-y-auto">
                                    {filteredDids.length === 0 ? (
                                        <div className="px-3 py-4 text-sm text-gray-500 text-center">
                                            {searchTerm ? "No DIDs match your search" : "No DIDs available"}
                                        </div>
                                    ) : (
                                        filteredDids.map((didItem) => (
                                            <button
                                                key={didItem.did}
                                                onClick={() => handleDidSelect(didItem.did,didItem.channelId)}
                                                className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-700 transition-colors duration-150 border-b border-gray-50 last:border-b-0"
                                            >
                                                <span className="font-mono">{didItem.did}</span>
                                            </button>
                                        ))
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Second Select Menu */}
                <div className="mb-8 w-full">
                    <label className="block text-md text-slate-600 font-medium mb-3 flex items-center gap-2">
                        <Bullet />
                        DID Assignment Mode
                    </label>

                    <select
                        className="w-full h-12 px-4 py-2.5 text-gray-700 bg-white border border-gray-300 rounded-lg shadow-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-200"
                        value={caller_id_mode || null}
                        onChange={(e) => setData(prevState => ({
                            ...prevState,
                            caller_id_mode: e.target.value
                        }))}
                    >
                        <option value="">Select DID Assignment Mode</option>
                        <option value="random">Random</option>
                        <option value="sequence">sequence</option>
                    </select>
                </div>
            </div>

            {/* Selected DIDs Table */}
            {selectedDids.length > 0 && (
                <div className="mb-8">
                    <h2 className="text-lg font-semibold text-gray-800 mb-4">
                        Selected DIDs ({selectedDids.length})
                    </h2>
                    <div className="overflow-x-auto bg-white rounded-lg shadow">
                        <table className="min-w-full text-sm text-left">
                            <thead className="bg-gray-50 border-b">
                                <tr>
                                    <th className="px-6 py-4 font-medium text-gray-700">No.</th>
                                    <th className="px-6 py-4 font-medium text-gray-700">DID (Agent)</th>
                                    <th className="px-6 py-4 font-medium text-gray-700">Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {selectedDids.map((item, index) => (
                                    <tr key={item?.did ?? item} className="border-b hover:bg-gray-50 transition-colors">
                                        <td className="px-6 py-4 font-medium text-gray-900">
                                            {index + 1}
                                        </td>
                                        <td className="px-6 py-4 text-gray-700">
                                            <span className="font-mono bg-gray-100 px-2 py-1 rounded text-sm">
                                                {item?.did ?? item}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4">
                                            <button
                                                onClick={() => handleDelete(item?.did ?? item, index)}
                                                className="inline-flex items-center justify-center w-8 h-8 rounded-full text-red-500 hover:bg-red-50 hover:text-red-700 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-red-200"
                                                aria-label={`Delete DID ${item?.did ?? item}`}
                                                title="Remove DID"
                                            >
                                                <Delete
                                                    className="w-4 h-4"
                                                    fill="currentColor"
                                                    style={{ color: 'currentColor' }}
                                                />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Navigation Buttons */}
            <div className="flex justify-between items-center pt-6">
                <button
                    onClick={handleBack}
                    className="px-8 py-3 font-semibold text-gray-600 bg-gray-100 rounded-full hover:bg-gray-200 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-gray-300"
                >
                    Back
                </button>

                <button
                    onClick={handleNextStep}
                    disabled={selectedDids.length === 0}
                    className="font-bold text-lg rounded-full w-36 h-12"
                    style={{
                        backgroundImage:
                            "linear-gradient(177.88deg, #AFD462 1.79%, #6BC698 49.97%, #0FB1E2 82.06%)",
                        color: "white",
                    }}
                >
                    Next Step
                </button>
            </div>
        </div>
    );
};

export default SelectDID;